import { AxiosInstance } from 'axios';
import {
  createAxiosInstance,
  attachAuthInterceptors,
} from '@/application/helpers/APIRequest';

const agentTierAPI: AxiosInstance = createAxiosInstance({
  baseUrlKey: 'AUTH_API_BASE_URL',
});
attachAuthInterceptors(agentTierAPI);

// Types
export interface GetAgentTiersRequest {
  page?: number;
  limit?: number;
  keyword?: string;
}

export type UserData = {
  id: string;
  username: string;
  email: string;
};
export interface AgentTierData {
  id: string;
  name: string;
  description: string;
  created_by: string;
  created_at: string;
  updated_by: string;
  updated_at: string;
  creator: UserData;
  updater: UserData;
}

export interface GetAgentTiersResponse {
  success: boolean;
  data?: {
    data: AgentTierData[];
    total: number;
    limit: number;
    page: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
  message?: string;
}

export interface AgentTierOption {
  id: string;
  name: string;
}

export interface GetAgentTierOptionsResponse {
  success: boolean;
  data?: AgentTierOption[];
  message?: string;
}

export interface DeleteAgentTierResponse {
  success: boolean;
  message?: string;
}

export interface CreateAgentTierRequest {
  name: string;
  description?: string;
}

export interface CreateAgentTierResponse {
  success: boolean;
  data?: AgentTierData;
  message?: string;
}

export interface UpdateAgentTierRequest {
  name?: string;
  description?: string;
}

export interface UpdateAgentTierResponse {
  success: boolean;
  data?: AgentTierData;
  message?: string;
}

export interface GetAgentTierResponse {
  success: boolean;
  data?: AgentTierData;
  message?: string;
  /** Set when the request failed at the HTTP layer (axios error). */
  httpStatus?: number;
}

// API Functions
export const getAgentTiers = async (
  params: GetAgentTiersRequest
): Promise<GetAgentTiersResponse> => {
  try {
    const { page = 1, limit = 10, keyword } = params;
    const queryParams = new URLSearchParams();

    queryParams.append('page', page.toString());
    queryParams.append('limit', limit.toString());

    if (keyword) {
      queryParams.append('keyword', keyword);
    }

    const response = await agentTierAPI.get(
      `/admin/agent-tiers?${queryParams.toString()}`
    );

    return {
      success: true,
      data: {
        data: response.data.agent_tiers || [],
        total: response.data.total,
        limit: response.data.limit,
        page: response.data.page,
        total_pages: response.data.total_pages,
        has_next: response.data.has_next || false,
        has_previous: response.data.has_previous || false,
      },
      message: 'Agent tiers fetched successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch agent tiers',
    };
  }
};

export const getAgentTierOptions = async (params?: {
  includeDeleted?: boolean;
}): Promise<GetAgentTierOptionsResponse> => {
  try {
    const response = await agentTierAPI.get(`/admin/agent-tiers/options`, {
      params: params?.includeDeleted ? { include_deleted: true } : undefined,
    });

    return {
      success: true,
      data: response.data,
      message: 'Agent tier options fetched successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch agent tier options',
    };
  }
};

export const deleteAgentTier = async (
  id: string
): Promise<DeleteAgentTierResponse> => {
  try {
    await agentTierAPI.delete(`/admin/agent-tiers/${id}`);

    return {
      success: true,
      message: 'Agent tier deleted successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to delete agent tier',
    };
  }
};

export const createAgentTier = async (
  data: CreateAgentTierRequest
): Promise<CreateAgentTierResponse> => {
  try {
    const response = await agentTierAPI.post('/admin/agent-tiers', data);

    return {
      success: true,
      data: response.data,
      message: 'Agent tier created successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to create agent tier',
    };
  }
};

export const updateAgentTier = async (
  id: string,
  data: UpdateAgentTierRequest
): Promise<UpdateAgentTierResponse> => {
  try {
    const response = await agentTierAPI.put(`/admin/agent-tiers/${id}`, data);

    return {
      success: true,
      data: response.data,
      message: 'Agent tier updated successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to update agent tier',
    };
  }
};

export const getAgentTier = async (
  id: string,
  opts?: { signal?: AbortSignal }
): Promise<GetAgentTierResponse> => {
  try {
    const response = await agentTierAPI.get(`/admin/agent-tiers/${id}`, {
      skipGlobal500ErrorToast: true,
      signal: opts?.signal,
    });

    return {
      success: true,
      data: response.data,
      message: 'Agent tier fetched successfully',
    };
  } catch (error: any) {
    const e = error as { code?: string; name?: string };
    if (
      e?.code === 'ERR_CANCELED' ||
      e?.name === 'CanceledError' ||
      e?.name === 'AbortError'
    ) {
      throw error;
    }
    return {
      success: false,
      httpStatus: error.response?.status as number | undefined,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch agent tier',
    };
  }
};

export default agentTierAPI;
